'use client';

import {
  ButtonHTMLAttributes,
  cloneElement,
  DetailedHTMLProps,
  HTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useState,
} from 'react';

import cn from '@/helpers/utils/classNames';

import HtmlRenderer from '../HtmlRender';

type TabLabelProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  isActive?: boolean;
  renderLabel?: (isActive?: boolean, onClick?: MouseEventHandler<HTMLButtonElement> | undefined) => ReactNode;
};

type TabDescriptionProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface TabProps {
  labels?: Array<ReactElement<TabLabelProps>>;
  contents?: Array<ReactElement<TabDescriptionProps>>;
  className?: string;
  contentWrapperClassName?: string;
}

export const TabLabel = ({ children, className, isActive, renderLabel, ...props }: TabLabelProps) => {
  return (
    <>
      {renderLabel ? (
        renderLabel(isActive, props.onClick)
      ) : (
        <button
          {...props}
          className={cn(`mr-1 rounded-t bg-gray-100 px-10 py-2 font-light xs:mr-0 xs:mb-1 xs:w-full`, className, {
            'bg-primary font-bold text-white': isActive,
          })}
        >
          {children}
        </button>
      )}
    </>
  );
};

export const TabContent = ({ children, ...props }: TabDescriptionProps) => {
  if (props.dangerouslySetInnerHTML?.__html)
    return <HtmlRenderer as="div" {...props} ref={undefined} html={props.dangerouslySetInnerHTML.__html || ''} />;

  return <div {...props}>{children}</div>;
};

const Tab = (props: TabProps) => {
  const { labels, contents, className, contentWrapperClassName } = props;
  const INITIALLY_ACTIVE_INDEX = 0;
  const [activeIndex, setActiveIndex] = useState(INITIALLY_ACTIVE_INDEX);

  return (
    <div className={cn(className)}>
      {labels?.length ? (
        <div className={cn('d-flex my-4')}>
          {labels.map((labelComponent, index) =>
            cloneElement(labelComponent, {
              isActive: index === activeIndex,
              onClick: () => setActiveIndex(index),
            })
          )}
        </div>
      ) : (
        ''
      )}

      <div className={cn('', contentWrapperClassName)}>
        {contents?.length ? (
          <div className={cn('animate-in fade-in duration-500', contentWrapperClassName)} key={activeIndex}>
            {cloneElement(contents[activeIndex], { key: activeIndex })}
          </div>
        ) : (
          ''
        )}
      </div>
    </div>
  );
};

Tab.Label = TabLabel;
Tab.Content = TabContent;

export default Tab;
